import useBySymbol from "backend/hooks/elements/useBySymbol";
import useFlatHierarchy from "backend/hooks/elements/useFlatHierarchy";
import useHierarchy from "backend/hooks/elements/useHierarchy";
import ReportsToolbar from "components/toolbars/ReportsToolbar";
import ElementTreeView from "design/components/trees/ElementTreeView";
import Page from "design/pages/Page";
import React, { FunctionComponent } from "react";
import { useAppContext } from "@qpr/client-api-base/context/AppCtxProvider";
import MeasureGridArea from "design/components/grids/MeasureGridArea";
import { ICardElementWithChildren } from "backend/model/model";
import StackView from "design/layouts/StackView";
import SideBar from "design/components/toolbar/SideBar";
import RowView from "design/layouts/RowView";
import CollapseCard from "design/components/cards/CollapseCard";
import TreeViewLeftPanel from "design/layouts/TreeViewLeftPanel";
import CardCheckedBox from "design/components/checkboxes/CardCheckedBox";
import ConfigReportCard from "design/components/cards/ConfigReportCard";
import CircularProgress from "design/components/progress/CircularProgress";
import WhiteBgArea from "design/layouts/WhiteBgArea";
import LaunchReport from "design/components/buttons/LaunchReport";
import ConfigurableReport from "components/reports/ConfigurableReport";
import useElement from "backend/hooks/elements/useElement";
import useLink from "@qpr/client-api-base/hooks/useLink";
import ReportListItem from "design/components/chips/ReportListItem";
import ExportFileSelectionPopUp from "design/components/popups/ExportFileSelectionPopup";
import PeriodSelector from "components/PeriodSelector";
import sortHierarchy from "backend/utils/sortHierarchy";

interface ConfigurableReportsProps {}

const ConfigurableReports: FunctionComponent<ConfigurableReportsProps> = () => {
  const ctx = useAppContext();
  const link = useLink();
  const { data: rootItem } = useElement(ctx.elementId);

  const divRef = React.useRef<HTMLDivElement>(null);
  const [fileType, setFileType] = React.useState("");
  const [isExporting, setIsExporting] = React.useState(false);
  const [fileSelectAnchor, setFileSelectAnchor] =
    React.useState<HTMLDivElement | null>(null);

  const [selectedItems, setSelectedItems] = React.useState<
    ICardElementWithChildren[]
  >([]);
  const selectedKPIs = React.useMemo(
    () => selectedItems.filter((item) => item.typename === "KPI"),
    [selectedItems]
  );
  const selectedInitiatives = React.useMemo(
    () => selectedItems.filter((item) => item.typename === "Initiative"),
    [selectedItems]
  );
  const { data: theme } = useBySymbol("L0");
  const { data: corpo } = useElement(theme?.id);
  const {
    data,
    isLoading: isDataLoading,
    isError,
    isPreviousData,
    isRefetching,
  } = useHierarchy(theme?.id);

  const treeViewData = React.useMemo(() => {
    if (corpo && data) {
      return [
        corpo,
        ...sortHierarchy(data, (a, b) => a.symbol.localeCompare(b.symbol)),
      ];
    }
    return [];
  }, [corpo, data]);
  const { data: subElements, isLoading: isSubElementsLoading } =
    useFlatHierarchy(rootItem?.topelement);

  const listItems = React.useMemo(() => {
    return subElements
      ?.filter((el) => el.typename === "KPI" || el.typename === "Initiative")
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [subElements]);

  const toggleItem = (item: ICardElementWithChildren) => {
    setSelectedItems((items) => {
      if (items.find((it) => it.id === item.id)) {
        return items.filter((i) => i.id !== item.id);
      }
      return [...items, item];
    });
  };

  return (
    <Page isLoading={!!!data} Toolbar={ReportsToolbar}>
      <StackView gap="0px">
        <div></div>
        <RowView>
          <TreeViewLeftPanel>
            <ElementTreeView
              selectedItem={rootItem}
              items={treeViewData}
              onSelected={(item) => {
                link({ meaId: item.id });
              }}
            />
          </TreeViewLeftPanel>
          <WhiteBgArea>
            <h2>{rootItem?.name}</h2>
            <MeasureGridArea minWidth="calc(100vw - 600px )">
              {listItems ? (
                listItems.map((el) => (
                  <ConfigReportCard
                    item={el}
                    onSelect={() => {
                      toggleItem(el);
                    }}
                  >
                    <CardCheckedBox
                      type="blue"
                      dy={-20}
                      dx={0}
                      isOpen={!!selectedItems.find((kpi) => kpi.id === el.id)}
                    />
                  </ConfigReportCard>
                ))
              ) : isSubElementsLoading ? (
                <CircularProgress />
              ) : null}
            </MeasureGridArea>
          </WhiteBgArea>
          <div>
            <SideBar>
              {selectedItems.length > 0 ? (
                <CollapseCard title="Options" isOpen>
                  <div>Select Period for the Report</div>
                  <PeriodSelector level="Month" />
                  <LaunchReport
                    title="Launch Report"
                    onClick={() => {
                      setFileSelectAnchor(divRef.current);
                    }}
                  />{" "}
                </CollapseCard>
              ) : null}
              <div ref={divRef} style={{ transform: "translateY(-30px)" }} />

              <CollapseCard
                title={`KPIs (${selectedKPIs.length})`}
                isOpen={selectedKPIs.length > 0}
              >
                {selectedKPIs.map((it) => (
                  <ReportListItem
                    item={it}
                    onRemove={(item) => {
                      toggleItem(item);
                    }}
                  />
                ))}
              </CollapseCard>
              <CollapseCard
                isOpen={selectedInitiatives.length > 0}
                title={`Initiatives (${selectedInitiatives.length})`}
              >
                {selectedInitiatives.map((it) => (
                  <ReportListItem
                    item={it}
                    onRemove={(item) => {
                      toggleItem(item);
                    }}
                  />
                ))}
              </CollapseCard>

              {fileSelectAnchor ? (
                <>
                  <ExportFileSelectionPopUp
                    onClose={() => {
                      setFileSelectAnchor(null);
                    }}
                    handleClick={(value) => {
                      setFileType(value);
                      setFileSelectAnchor(null);
                      setIsExporting(true);
                    }}
                    anchorEl={fileSelectAnchor}
                  />
                </>
              ) : null}
            </SideBar>
            {isExporting && fileType ? (
              <ConfigurableReport
                items={selectedItems}
                filetype={fileType}
                onClose={() => {
                  setIsExporting(false);
                }}
              />
            ) : null}
          </div>
        </RowView>
      </StackView>
    </Page>
  );
};

export default ConfigurableReports;
